import React from 'react';
import { useLocation } from 'react-router-dom';
import { FormattedMessage, injectIntl } from 'react-intl';
import { LANGUAGES } from '../../_i18n/languages';
import LangCheck from '../langCheck';

const getTargetUrlForLang = (pathname, targetLang) => {
  if (!pathname) pathname = '/';
  const segments = pathname.split('/').filter(Boolean);

  const nonI18nPages = ['about', 'terms', 'privacy', 'blog'];
  if (segments.length > 0 && nonI18nPages.includes(segments[0])) {
    return targetLang === 'en' ? `/${segments[0]}` : `/${targetLang}`;
  }

  // Home route: '/' or '/pt', '/es', etc.
  const isHome = segments.length === 0 || (segments.length === 1 && LANGUAGES[segments[0]]);
  if (isHome) {
    return targetLang === 'en' ? '/' : `/${targetLang}`;
  }

  // Subroute tool (e.g. /stopwatch, /alarm-clock-for-day-traders, /countdown-timer)
  const toolName = segments[0];
  let paramPath = '';
  if (segments[1] && LANGUAGES[segments[1]]) {
    paramPath = segments.slice(2).join('/');
  } else if (segments[1]) {
    paramPath = segments.slice(1).join('/');
  }

  const paramSuffix = paramPath ? `/${paramPath}` : '';
  return targetLang === 'en' ? `/${toolName}${paramSuffix}` : `/${toolName}/${targetLang}${paramSuffix}`;
};

const Header = (props) => {
  const { intl } = props;
  const location = useLocation();
  const pathname = (location && location.pathname) || '/';
  const currentLang = LangCheck.getCurrentLang({ location }) || 'en';

  const segments = pathname.split('/').filter(Boolean);
  const isHome = segments.length === 0 || (segments.length === 1 && LANGUAGES[segments[0]]);
  const isTraderAlarm = segments[0] === 'alarm-clock-for-day-traders' || segments[0] === 'trader-alarm-clock';
  const isRemoteAlarm = segments[0] === 'alarm-clock-for-remote-workers' || segments[0] === 'remote-worker-alarm';
  const isShiftAlarm = segments[0] === 'alarm-clock-for-medical-legal-shifts' || segments[0] === 'shift-worker-alarm';
  const isStopwatch = segments[0] === 'stopwatch';
  const isTimer = segments[0] === 'countdown-timer';
  const isBlog = segments[0] === 'blog';

  const alarmClockUrl = currentLang === 'en' ? '/' : `/${currentLang}`;
  const traderAlarmUrl = currentLang === 'en' ? '/alarm-clock-for-day-traders' : `/alarm-clock-for-day-traders/${currentLang}`;
  const remoteAlarmUrl = currentLang === 'en' ? '/alarm-clock-for-remote-workers' : `/alarm-clock-for-remote-workers/${currentLang}`;
  const shiftAlarmUrl = currentLang === 'en' ? '/alarm-clock-for-medical-legal-shifts' : `/alarm-clock-for-medical-legal-shifts/${currentLang}`;
  const stopwatchUrl = currentLang === 'en' ? '/stopwatch' : `/stopwatch/${currentLang}`;
  const timerUrl = currentLang === 'en' ? '/countdown-timer' : `/countdown-timer/${currentLang}`;

  const handleLanguageChange = (e) => {
    const selectedLang = e.target.value;
    if (!selectedLang || selectedLang === currentLang) return;
    const targetUrl = getTargetUrlForLang(pathname, selectedLang);
    if (typeof window !== 'undefined') {
      window.location.href = targetUrl;
    }
  };

  const titleAlarmClock = intl ? intl.formatMessage({ id: 'header.title.alarmClock', defaultMessage: 'Online Alarm Clock' }) : 'Online Alarm Clock';
  const titleTrader = intl ? intl.formatMessage({ id: 'header.title.trader', defaultMessage: 'Market & Crypto Trader Alarm' }) : 'Market & Crypto Trader Alarm';
  const titleRemoteWork = intl ? intl.formatMessage({ id: 'header.title.remoteWork', defaultMessage: 'Remote Worker Virtual Office Alarm' }) : 'Remote Worker Virtual Office Alarm';
  const titleShiftAlarm = intl ? intl.formatMessage({ id: 'header.title.shiftAlarm', defaultMessage: 'Medical & Legal Shift Alarm' }) : 'Medical & Legal Shift Alarm';
  const titleStopwatch = intl ? intl.formatMessage({ id: 'header.title.stopwatch', defaultMessage: 'Online Stopwatch' }) : 'Online Stopwatch';
  const titleTimer = intl ? intl.formatMessage({ id: 'header.title.timer', defaultMessage: 'Online Countdown Timer' }) : 'Online Countdown Timer';
  const titleBlog = intl ? intl.formatMessage({ id: 'header.title.blog', defaultMessage: 'Sleep & Productivity Blog' }) : 'Sleep & Productivity Blog';
  const selectLangLabel = intl ? intl.formatMessage({ id: 'header.selectLang', defaultMessage: 'Select Language' }) : 'Select Language';

  return (
    <header>
      <nav className="navbar navbar-expand-md navbar-light fixed-top">
        <div className="container-fluid">
          <a className="navbar-brand logo-text" href={alarmClockUrl}>
            <img src="/logo32.png" alt="alarm-clock.net" className="logo" />
            <FormattedMessage id="header.brand" defaultMessage="Online Alarm Clock" />
          </a>
          <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarCollapse" aria-controls="navbarCollapse" aria-expanded="false" aria-label="Toggle navigation">
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className="collapse navbar-collapse justify-content-end" id="navbarCollapse">
            <ul className="navbar-nav mb-2 mb-md-0 align-items-md-center">
              <li className="nav-item">
                <a className={`nav-link ${isHome ? 'active fw-bold' : ''}`} href={alarmClockUrl} title={titleAlarmClock}>
                  <FormattedMessage id="header.alarmClock" defaultMessage="Alarm Clock" />
                </a>
              </li>
              <li className="nav-item">
                <a className={`nav-link ${isTraderAlarm ? 'active fw-bold' : ''}`} href={traderAlarmUrl} title={titleTrader}>
                  <FormattedMessage id="header.trader" defaultMessage="Trader" />
                </a>
              </li>
              <li className="nav-item">
                <a className={`nav-link ${isRemoteAlarm ? 'active fw-bold' : ''}`} href={remoteAlarmUrl} title={titleRemoteWork}>
                  <FormattedMessage id="header.remoteWork" defaultMessage="Remote Work" />
                </a>
              </li>
              <li className="nav-item">
                <a className={`nav-link ${isShiftAlarm ? 'active fw-bold' : ''}`} href={shiftAlarmUrl} title={titleShiftAlarm}>
                  <FormattedMessage id="header.shiftAlarm" defaultMessage="Shift Alarm" />
                </a>
              </li>
              <li className="nav-item">
                <a className={`nav-link ${isStopwatch ? 'active fw-bold' : ''}`} href={stopwatchUrl} title={titleStopwatch}>
                  <FormattedMessage id="header.stopwatch" defaultMessage="Stopwatch" />
                </a>
              </li>
              <li className="nav-item">
                <a className={`nav-link ${isTimer ? 'active fw-bold' : ''}`} href={timerUrl} title={titleTimer}>
                  <FormattedMessage id="header.timer" defaultMessage="Countdown Timer" />
                </a>
              </li>
              <li className="nav-item">
                <a className={`nav-link ${isBlog ? 'active fw-bold' : ''}`} href="/blog" title={titleBlog}>
                  <FormattedMessage id="header.blog" defaultMessage="Blog" />
                </a>
              </li>
              <li className="nav-item ms-md-2 mt-2 mt-md-0">
                <div className="header-lang-wrapper d-flex align-items-center">
                  <span className="header-lang-icon" aria-hidden="true">🌐</span>
                  <select
                    className="form-select form-select-sm header-lang-select"
                    value={currentLang}
                    onChange={handleLanguageChange}
                    aria-label={selectLangLabel}
                    title={selectLangLabel}
                  >
                    <option value="en">🇺🇸 EN (English)</option>
                    <option value="pt">🇧🇷 PT (Português)</option>
                    <option value="es">🇪🇸 ES (Español)</option>
                    <option value="fr">🇫🇷 FR (Français)</option>
                    <option value="de">🇩🇪 DE (Deutsch)</option>
                    <option value="it">🇮🇹 IT (Italiano)</option>
                    <option value="nl">🇳🇱 NL (Nederlands)</option>
                    <option value="ru">🇷🇺 RU (Русский)</option>
                  </select>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default injectIntl(Header);